import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

// إنشاء مخطط التحقق من البيانات باستخدام yup
const schema = yup.object().shape({
  DispalyName: yup.string().required("Display Name is required"),
  Username: yup.string().required("Username is required"),
  RoleID: yup.number().required("Role is required"),
  BranchID: yup.number().required("Branch is required"),
});

export default function EditUserModal({ open, handleClose, user }) {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]); // قائمة الأدوار
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  // استخدام react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // تحديث بيانات المستخدم عند فتح المودال
  useEffect(() => {
    if (user) {
      reset({
        DispalyName: user.DispalyName,
        Username: user.Username,
        RoleID: user.RoleID,
        BranchID: user.BranchID,
        IsLocked: user.IsLocked,
      });
    }
  }, [user, reset]);

  // تحميل قائمة الأدوار عند فتح المودال
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const response = await axios.get(
          "https://tarabotcs.com-eg.net/api/Users/V1/GetRoles",
          {
            headers: {
              CompanyID: "221005",
              AccessToken: userData.Data.AccessToken,
            },
          }
        );
        if (response.data.Success) {
          setRoles(response.data.Data);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        if (error.response && error.response.status === 401) {
          console.log("Session expired, logging out...");
          localStorage.removeItem("userData"); // Clear user data
          navigate("/"); // Redirect to the home page
        }
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const response = await axios.get(
          "https://tarabotcs.com-eg.net/api/Users/V1/GetBranches",
          {
            headers: {
              CompanyID: "221005",
              AccessToken: userData.Data.AccessToken,
            },
          }
        );
        if (response.data.Success) {
          setBranches(response.data.Data);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        if (error.response && error.response.status === 401) {
          console.log("Session expired, logging out...");
          localStorage.removeItem("userData"); // Clear user data
          navigate("/"); // Redirect to the home page
        }
      }
    };

    fetchBranches();
  }, []);

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const response = await axios.post(
        `https://tarabotcs.com-eg.net/api/Users/V1/UpdateUser`,
        {
          UserID: user.UserID,
          DispalyName: formData.DispalyName,
          Username: formData.Username,
          RoleID: formData.RoleID,
          BranchID: formData.BranchID,
          IsLocked: formData.IsLocked,
          UserNo: "",
          Notes: "",
        },
        {
          headers: {
            CompanyID: userData.Data.CompanyID,
            AccessToken: userData.Data.AccessToken,
          },
        }
      );
      console.log("User updated successfully", response.data);
      handleClose();
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response && error.response.status === 401) {
        console.log("Session expired, logging out...");
        localStorage.removeItem("userData"); // Clear user data
        navigate("/"); // Redirect to the home page
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit User</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <TextField
            margin="dense"
            label="Display Name"
            name="DispalyName"
            {...register("DispalyName")}
            error={!!errors.DispalyName}
            helperText={errors.DispalyName?.message}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Username"
            name="Username"
            {...register("Username")}
            error={!!errors.Username}
            helperText={errors.Username?.message}
            fullWidth
          />

          <Controller
            name="RoleID"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                select
                label="Select Role"
                fullWidth
                error={!!errors.RoleID}
                helperText={errors.RoleID?.message}
                margin="normal"
                variant="outlined"
                {...field} // تعيين الفيلد من الـ Controller
              >
                {roles.map((role) => (
                  <MenuItem key={role.RoleID} value={role.RoleID}>
                    {role.EnglishName}{" "}
                    {/* أو role.RoleName حسب توافر البيانات */}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="BranchID"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                select
                label="Select Branch"
                fullWidth
                error={!!errors.BranchID}
                helperText={errors.BranchID?.message}
                margin="normal"
                variant="outlined"
                {...field} // تعيين الفيلد من الـ Controller
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.BranchID} value={branch.BranchID}>
                    {branch.EnglishName}{" "}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* إضافة Switch لتفعيل أو تعطيل المستخدم */}
          <FormControlLabel
            control={
              <Controller
                name="IsLocked"
                control={control}
                render={({ field }) => (
                  <Switch {...field} checked={field.value} />
                )}
              />
            }
            label="Is Locked"
          />
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            onClick={handleClose}
            sx={{ backgroundColor: "red", color: "white" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            sx={{ backgroundColor: "#263150", color: "white" }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
